import { AddCircleOutline, AutoAwesome, Close, ExpandMore, LibraryBooksOutlined, Menu as MenuIcon, VideocamOutlined, AudioFileOutlined, ImageOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Skeleton,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { arrayMove } from '@dnd-kit/sortable';
import AssignmentAiSection from 'components/AssignmentAiGenerate/AssignmentAiModal';
import AssignmentPreview from 'components/AssignmentPreview';
import AssignmentSourcePickerModal from 'components/AssignmentBuilder/AssignmentSourcePickerModal';
import { AssignmentBuilderQuestionPreview } from 'components/AssignmentBuilder/AssignmentBuilderQuestionPreview';
import {
  AssignmentBuilderSuperAdminPanel,
  mergeAiGradingConfigForPayload,
  type SuperAdminActivityState,
} from 'components/AssignmentBuilder/AssignmentBuilderSuperAdminPanel';
import { QuestionBuilderRenderer } from 'components/AssignmentBuilder/builders';
import { useGetMaskId } from 'components/hooks/maskId/useGetMaskId';
import { useAlertLoading } from 'components/UI/alertSakeBar/alertProvider';
import { cloneDeep, find, get, has } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  handleCreateActivities,
  handleCreateCustomAssignment,
  handleUpdateActivities,
  handleUpdateCustomAssignment,
} from 'store/activities/thunks';
import { handleAutoSelectStandardsForAllQuestions } from 'store/story/thunks';
import { useOrgStandardCodes } from 'store/organizations/selectors';
import { useStandards } from 'store/story/selectors';
import { normalizeStateForComparison, statesMatch } from 'utils/lib/standardHelper';
import {
  collectAssignmentValidationErrors,
  formatAssignmentValidationMessages,
} from 'utils/methods/assignmentValidation';
import { getImageThumbSrcList } from 'utils/methods/storageImageThumbSrcList';
import { Img } from 'react-image';
import { useActivities } from 'store/activities/selectors';
import { useAllBook } from 'store/books/selectors';
import { useQuizzes } from 'store/quizzes/selectors';
import { useResources } from 'store/resources/selectors';
import {
  ebookConfigDinamic,
  getNumberMask,
  imageFetch,
  imageFetchFromPodcast,
  isVideo,
} from 'utils/lib/ebookHelper';
import { apis } from 'utils/lib/apis';
import { QUESTION_TYPE_ACTIVITY_NON_NATIVE, photoCollageData } from 'utils/config/reference';
import { findMatchingLevel } from 'modules/assignments/helper';
import { resource_types } from 'store/extraResource/constants';
import {
  Z_BUILDER_DIALOG_ABOVE_APP_NAV,
  Z_BUILDER_DIALOG_MENU_THEME_MODAL,
  Z_BUILDER_MUI_PORTALS,
  Z_BUILDER_NESTED_MODAL,
  Z_BUILDER_SHELL,
} from './assignmentBuilderZIndex';
import { htmlToPlainPreviewText } from './htmlToPlainPreviewText';
import styles from './AssignmentBuilderTool.module.css';
import { useCurrentUser } from 'store/users/selectors';
import { useBookUnits } from 'store/booksUnits/selectors';
import { useApExams } from 'store/apexams/selectors';
import { useStory } from 'store/story/selectors';
import { useStandardExams } from 'store/standardexams';
import { checkStandardExam } from 'utils/methods/textMethod';
import useIsExamActive from 'components/hooks/books/useIsExamActive';
import AppTextField from 'components/UI/inputs/AppTextField';

const PICKER_EMPTY_CONFIG: Record<string, unknown> = {};

const PICKER_EMPTY_ITEMS: unknown[] = [];

function emptySuperAdminActivity(): SuperAdminActivityState {
  return {
    level: null,
    storyType: false,
    apExamType: false,
    examCategories: '',
    assignmentStandards: [],
    isExam: false,
    isUnitExam: false,
    unit: null,
    unit_exam: null,
    rubrics_id: null,
    ai_grading_config: {},
    pageLinkConfiguration: false,
  };
}

const coverThumbSkeleton = (
  <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 0 }} animation="wave" />
);

function SourceAssignmentCoverThumb({ modalSrc }: { modalSrc: string }) {
  const [visible, setVisible] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const srcList = useMemo(() => getImageThumbSrcList(modalSrc), [modalSrc]);

  useEffect(() => {
    setVisible(false);
  }, [modalSrc]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries, o) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            o.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [modalSrc]);

  return (
    <div ref={wrapRef} className={styles.selectionItemThumbObserve}>
      {visible ? (
        <Img src={srcList} alt="page cover" loader={coverThumbSkeleton} unloader={coverThumbSkeleton} />
      ) : (
        coverThumbSkeleton
      )}
    </div>
  );
}

export type BuilderQuestion = {
  id?: number;
  _uid: string;
  type: string;
  question?: string;
  options?: any[];
  answer?: number | string | string[];
  duration?: number | string;
  audio?: string;
  groupKey?: string;
  [key: string]: unknown;
};

export type AssignmentBuilderContinuePayload = {
  assignmentTitle: string;
  ai_questions: BuilderQuestion[];
};

type BookContentItem = {
  pageId?: string | number;
  numberMasked?: string;
  resource?: string;
};

type BookDataShape = {
  content?: BookContentItem[];
};

type BuilderAiSource = {
  chapter: string | number;
  page: string | number;
  data: BookDataShape;
  from: string;
  fromResource: string;
  pageSrc: string | null | undefined;
  balconPages: any[];
  pageRenage?: {
    start: string | number;
    finish: string | number;
  };
};

function resolveBuilderAiSource(
  loc: { bookId: string; resourceType: string; items: any[]; selectedConfig: Record<string, unknown> },
  allBooks: any[],
  fallbackPage: string | number,
  defaultBalconPages: any[],
): BuilderAiSource {
  const { bookId, resourceType, items, selectedConfig } = loc;
  const rt = resourceType;

  if (rt === 'ebook') {
    const cfg = ebookConfigDinamic(bookId, allBooks) as BookDataShape;
    const content = Array.isArray(cfg?.content) ? cfg.content : [];
    const firstPage = content.length ? content[0].pageId : fallbackPage;
    return {
      chapter: bookId,
      page: firstPage ?? fallbackPage,
      data: cfg,
      from: 'ebook',
      fromResource: 'ebook',
      pageSrc: firstPage != null ? imageFetch(allBooks, String(bookId), firstPage) || null : null,
      balconPages: [],
    };
  }

  if (rt === resource_types.resource) {
    const rid = items?.[0]?.id ?? bookId;
    return {
      chapter: rid,
      page: rid,
      data: { content: [] },
      from: 'podcast',
      fromResource: 'resource',
      pageSrc: null,
      balconPages: [],
    };
  }

  const unitTypes = [
    resource_types.balcon,
    resource_types.conectándome,
    resource_types.comunidad,
    resource_types.informate,
  ];
  if (unitTypes.includes(rt)) {
    const cfg = ebookConfigDinamic(bookId, allBooks) as BookDataShape;
    const content = Array.isArray(cfg?.content) ? cfg.content : [];
    const firstPage = content.length ? content[0].pageId : fallbackPage;
    const firstItem = items?.[0];
    let balconPages = [];
    if(firstItem){
      const firstIndex = content.findIndex((item) => item.pageId == firstItem.start);
      const lastIndex = content.findIndex((item) => item.pageId == firstItem.finish);
      if(firstIndex !== -1 && lastIndex !== -1){
         balconPages = content.slice(firstIndex, lastIndex + 1);
      }
    }
    return {
      chapter: bookId,
      page: firstPage ?? fallbackPage,
      data: cfg,
      from: rt,
      fromResource: rt,
      pageSrc: firstPage != null ? imageFetch(allBooks, String(bookId), firstPage) || null : null,
      balconPages,
      pageRenage:{
        start: firstItem?.start,
        finish: firstItem?.finish,
      }
    };
  }

  if (rt === resource_types.story) {
    const storyBookId = String(items?.[0]?.bookId ?? bookId);
    const cfg = ebookConfigDinamic(storyBookId, allBooks) as BookDataShape;
    const content = Array.isArray(cfg?.content) ? cfg.content : [];
    const levelKey = selectedConfig?.level as string | undefined;
    const levels = items?.[0]?.levels;
    let startPage = fallbackPage;
    let finishPage = fallbackPage;
    let balconPages = [];
    if (levelKey && Array.isArray(levels)) {
      const matched = findMatchingLevel(levels, levelKey);
      if (matched?.start != null) startPage = matched.start;
      if (matched?.finish != null) finishPage = matched.finish;
      const firstIndex = content.findIndex((item) => item.pageId == startPage);
      const lastIndex = content.findIndex((item) => item.pageId == finishPage);
      if(firstIndex !== -1 && lastIndex !== -1){
         balconPages = content.slice(firstIndex, lastIndex + 1);
      }
    } else if (content.length) {
      startPage = content[0].pageId ?? fallbackPage;
    }
    return {
      chapter: storyBookId,
      page: startPage,
      data: cfg,
      from: resource_types.story,
      fromResource: resource_types.story,
      pageSrc: imageFetch(allBooks, storyBookId, startPage) || null,
      balconPages,
      pageRenage:{
        start: startPage,
        finish: finishPage,
      }
    };
  }

  if (rt === resource_types.AP) {
    const apBookId = String(
      (selectedConfig as { selectedCategoryAP?: { chapter_ids?: string } })?.selectedCategoryAP?.chapter_ids ??
        bookId,
    );
    const cfg = ebookConfigDinamic(apBookId, allBooks) as BookDataShape;
    const content = Array.isArray(cfg?.content) ? cfg.content : [];
    const firstItem = items?.[0];
    const startPage = firstItem?.start ?? (content.length ? content[0].pageId : fallbackPage);
    let balconPages = [];
    if(firstItem){
      const firstIndex = content.findIndex((item) => item.pageId == firstItem.start);
      const lastIndex = content.findIndex((item) => item.pageId == firstItem.finish);
      if(firstIndex !== -1 && lastIndex !== -1){
         balconPages = content.slice(firstIndex, lastIndex + 1).map((p,i)=>({...p,page_dynamic_id:i+1}));
      }
    }
    return {
      chapter: apBookId,
      page: startPage ?? fallbackPage,
      data: cfg,
      from: resource_types.AP,
      fromResource: resource_types.AP,
      pageSrc: startPage != null ? imageFetch(allBooks, apBookId, startPage) || null : null,
      balconPages,
      pageRenage:{
        start: firstItem?.start,
        finish: firstItem?.finish,
      }
    };
  }

  const cfg = ebookConfigDinamic(bookId, allBooks) as BookDataShape;
  const content = Array.isArray(cfg?.content) ? cfg.content : [];
  const firstPage = content.length ? content[0].pageId : fallbackPage;
  return {
    chapter: bookId,
    page: firstPage ?? fallbackPage,
    data: cfg,
    from: 'ebook',
    fromResource: 'ebook',
    pageSrc: firstPage != null ? imageFetch(allBooks, String(bookId), firstPage) || null : null,
    balconPages: defaultBalconPages,
  };
}

export type AssignmentBuilderToolProps = {
  open: boolean;
  onClose: () => void;
  data: BookDataShape | null | undefined;
  from?: string;
  page: string | number;
  chapter: string | number;
  pageSrc?: string | null;
  onContinueToAssignmentForm?: (payload: AssignmentBuilderContinuePayload) => void;
  assignmentRecord?: Record<string, unknown> | null;
  disableSubmit?: boolean;
  formResource?: string;
  storyInstruction?: string;
  article_level_id?: string;
  onCreated?: () => void;
  pageTitle?: string;
  BALCON_PAGES?: any[];
  generatedTest?: boolean;
};

function ensureQuestionUids(questionsList: BuilderQuestion[]): BuilderQuestion[] {
  if (!Array.isArray(questionsList)) return questionsList;
  return questionsList.map((q) => ({
    ...q,
    _uid: q._uid || `q-${Math.random().toString(36).slice(2)}`,
  }));
}

function getNextId(questions: BuilderQuestion[]): number {
  const last = questions[questions.length - 1];
  const n = parseInt(String(last?.id ?? 0), 10);
  return Number.isFinite(n) ? n + 1 : 1;
}

function defaultQuestionForType(type: string, id: number): BuilderQuestion {
  const _uid = `q-${Math.random().toString(36).slice(2)}`;
  switch (type) {
    case 'multipleChoice':
      return { type, id, _uid, question: '', options: ['', '', '', ''], answer: 0 };
    case 'multipleResponse':
      return { type, id, _uid, question: '', options: ['', '', '', ''], answer: [] };
    case 'trueFalse':
      return { type, id, _uid, question: '', options: ['Cierto', 'Falso'], answer: 0 };
    case 'audioTrueFalse':
      return { type, id, _uid, question: '', duration: 60, options: ['Cierto', 'Falso'], answer: 0, audio: '' };
    case 'audio':
      return { type, id, _uid, question: '', duration: 60, audio: '' };
    case 'audioEssay':
      return {
        type,
        id,
        _uid,
        question: '',
        duration: 60,
        audio: '',
        groupKey: `multichoice-audio-${Math.random().toString(36).substring(2, 15)}`,
      };
    case 'audioToImage':
      return {
        type,
        id,
        _uid,
        question: '',
        duration: 60,
        audio: '',
        categoryType: 'Image',
        studentCanUploadImages: 6,
      };
    case 'audioMultipleChoice':
      return {
        type,
        id,
        _uid,
        question: '',
        duration: 60,
        options: ['', '', '', ''],
        answer: 0,
        audio: '',
        groupKey: `multichoice-audio-${Math.random().toString(36).substring(2, 15)}`,
      };
    case 'picture':
      return {
        type,
        id,
        _uid,
        question: '',
        categoryType: 'Image',
        studentCanUploadImages: 6,
        additionalPictures: [],
      };
    case 'video':
      return { type, id, _uid, question: '', duration: 60 };
    case 'match':
      return {
        type,
        id,
        _uid,
        question: '',
        matchType: 'Text',
        questionType: 'Text',
        options: [{ item: '', match_item: '', match_audio: '', match_text: '', questionImage: '', questionAudio: '' }],
      };
    case 'filling':
    case 'fillingImage':
      return {
        type,
        id,
        _uid,
        question: '',
        options: [''],
        fillingType: 'None',
        response_type: 'dropdown',
        answer: '',
        answerSelected: [[]],
        highlightWords: [[]],
        selected_answer: [''],
        filling_image: [{ url: '' }],
      };
    case 'photoCollage':
      return {
        type,
        id,
        _uid,
        question: '',
        collageLength: 6,
        categoryType: [],
        collageFrame: photoCollageData.slice(0, 6),
      };
    case 'essay':
    default:
      return { type: 'essay', id, _uid, question: '', expected_answer: [] };
  }
}

function applyTypeChange(prev: BuilderQuestion, type: string): BuilderQuestion {
  const id = prev.id;
  const _uid = prev._uid;
  const instructionFallback = get(prev, 'instruction');
  const preservedQuestion =
    typeof prev.question === 'string'
      ? prev.question
      : typeof instructionFallback === 'string'
        ? instructionFallback
        : undefined;
  const base = defaultQuestionForType(type, id ?? 0);
  const nextGroupKey =
    isGroupedQuestionType(type) && prev.groupKey ? prev.groupKey : (base as BuilderQuestion).groupKey;
  return {
    ...base,
    id,
    _uid,
    groupKey: nextGroupKey,
    question: preservedQuestion !== undefined ? preservedQuestion : base.question,
  };
}

function typeLabel(type: string): string {
  const found = QUESTION_TYPE_ACTIVITY_NON_NATIVE.find((t) => t.value === type);
  return found?.type ?? type;
}

function isNonEmptyMediaUrl(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function getQuestionMediaFlags(question: any) {
  let hasAudio = isNonEmptyMediaUrl(question?.audio);
  let hasVideo = isNonEmptyMediaUrl(question?.video);
  let hasImage = Boolean(
    isNonEmptyMediaUrl(question?.image) ||
      (Array.isArray(question?.pictures) && question.pictures.some((item: any) => isNonEmptyMediaUrl(item))) ||
      (typeof question?.pictures === 'string' && isNonEmptyMediaUrl(question.pictures)) ||
      (Array.isArray(question?.additionalPictures) && question.additionalPictures.length > 0) ||
      (Array.isArray(question?.filling_image) &&
        question.filling_image.some((item: any) => isNonEmptyMediaUrl(item?.url))) ||
      isNonEmptyMediaUrl(question?.questionImage) ||
      isNonEmptyMediaUrl(question?.match_image),
  );

  if (question?.type === 'match' && Array.isArray(question?.options)) {
    question.options.forEach((opt: any) => {
      if (isNonEmptyMediaUrl(opt?.match_audio) || isNonEmptyMediaUrl(opt?.questionAudio)) hasAudio = true;
      if (isNonEmptyMediaUrl(opt?.match_item) || isNonEmptyMediaUrl(opt?.questionImage)) hasImage = true;
    });
  }

  if ((question?.type === 'filling' || question?.type === 'fillingImage') && question?.fillingType === 'Audio') {
    if (Array.isArray(question?.filling_image) && question.filling_image.some((item: any) => isNonEmptyMediaUrl(item?.url))) {
      hasAudio = true;
    }
  }

  if (question?.type === 'photoCollage' && Array.isArray(question?.collageFrame)) {
    if (question.collageFrame.some((frame: any) => isNonEmptyMediaUrl(frame?.image))) hasImage = true;
  }

  return { hasAudio, hasVideo, hasImage };
}

function isGroupedQuestionType(type: string): boolean {
  return type === 'audioMultipleChoice' || type === 'audioEssay';
}

function inflateDraft(question: BuilderQuestion, questions: BuilderQuestion[]): BuilderQuestion {
  if (!isGroupedQuestionType(question.type) || !question.groupKey) {
    return cloneDeep(question);
  }

  const groupQuestions = questions
    .filter((item) => item.type === question.type && item.groupKey === question.groupKey)
    .map((item) => cloneDeep(item));

  return {
    ...cloneDeep(question),
    subQuestions: groupQuestions.length ? groupQuestions : [cloneDeep(question)],
  };
}

function applyDraftToQuestions(questions: BuilderQuestion[], draft: BuilderQuestion | null): BuilderQuestion[] {
  if (!draft) return questions;

  if (!isGroupedQuestionType(draft.type) || !draft.groupKey) {
    return questions.map((question) => (question._uid === draft._uid ? cloneDeep(draft) : question));
  }

  const groupQuestions = Array.isArray(draft.subQuestions) && draft.subQuestions.length
    ? draft.subQuestions.map((item, index) => ({
        ...cloneDeep(item),
        type: draft.type,
        groupKey: draft.groupKey,
        id: item.id ?? `${draft.id ?? 'group'}-${index + 1}`,
        _uid: item._uid || `q-${Math.random().toString(36).slice(2)}`,
      }))
    : [cloneDeep(draft)];

  const firstGroupIndex = questions.findIndex(
    (question) =>
      question.groupKey === draft.groupKey ||
      question._uid === draft._uid ||
      (question.type === draft.type && question.groupKey === draft.groupKey),
  );

  if (firstGroupIndex === -1) {
    return [...questions, ...groupQuestions];
  }

  const remainingQuestions = questions.filter(
    (question) =>
      question.groupKey !== draft.groupKey &&
      question._uid !== draft._uid &&
      !(question.type === draft.type && question.groupKey === draft.groupKey),
  );

  return [
    ...remainingQuestions.slice(0, firstGroupIndex),
    ...groupQuestions,
    ...remainingQuestions.slice(firstGroupIndex),
  ].map((question, index) => ({
    ...question,
    id: index + 1,
  }));
}

function buildAssignmentBuilderPersistSnapshot(
  assignmentTitle: string,
  languageFeedback: string,
  usedAiQuestions: boolean,
  questions: BuilderQuestion[],
  draft: BuilderQuestion | null,
): string {
  const merged = applyDraftToQuestions(questions, draft);
  const cleaned = merged.map((q) => {
    const { _uid, subQuestions, ...rest } = q as BuilderQuestion & { subQuestions?: unknown };
    return rest;
  });
  return JSON.stringify({
    title: assignmentTitle.trim(),
    languageFeedback,
    usedAiQuestions,
    questions: cleaned,
  });
}

type SourceAssignment = {
  id: string;
  title: string;
  sourceType: string;
  questions: BuilderQuestion[];
};

type SourceAssignmentGroup = {
  id: string;
  title: string;
  assignments: SourceAssignment[];
  children: SourceAssignmentGroup[];
};

const toPageNumber = (value: unknown): number | null => {
  const parsed = parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const getAssignmentPageNumbers = (assignment: SourceAssignment): number[] => {
  const directPages = [
    get(assignment, 'page'),
    get(assignment, 'data.page'),
    get(assignment, 'pageId'),
    get(assignment, 'data.pageId'),
    get(assignment, 'assessment_page_id'),
    get(assignment, 'data.assessment_page_id'),
  ]
    .map(toPageNumber)
    .filter((value): value is number => value !== null);

  const linkedPages = [
    ...(Array.isArray(get(assignment, 'linked_to')) ? get(assignment, 'linked_to') : []),
    ...(Array.isArray(get(assignment, 'data.linked_to')) ? get(assignment, 'data.linked_to') : []),
  ]
    .map((linkedItem: any) => toPageNumber(linkedItem?.pageId))
    .filter((value): value is number => value !== null);

  return Array.from(new Set([...directPages, ...linkedPages]));
};

const assignmentMatchesRange = (assignment: SourceAssignment, start: unknown, finish: unknown): boolean => {
  const startPage = toPageNumber(start);
  const finishPage = toPageNumber(finish);
  if (startPage === null || finishPage === null) return false;
  return getAssignmentPageNumbers(assignment).some((pageValue) => pageValue >= startPage && pageValue <= finishPage);
};

type SourceActivityListSort = 'title_az' | 'title_za' | 'modified_asc' | 'modified_desc';

const getSourceAssignmentModifiedMs = (assignment: SourceAssignment): number => {
  const raw =
    get(assignment, 'modifiedOn') ??
    get(assignment, 'data.modifiedOn') ??
    get(assignment, 'updatedAt') ??
    get(assignment, 'data.updatedAt') ??
    get(assignment, 'lastModified') ??
    get(assignment, 'data.lastModified');
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const s = String(Math.floor(Math.abs(raw)));
    return s.length <= 10 ? raw * 1000 : raw;
  }
  const str = String(raw).trim();
  if (/^\d+$/.test(str)) {
    const n = Number(str);
    if (!Number.isFinite(n)) return 0;
    return str.length <= 10 ? n * 1000 : n;
  }
  const parsed = Date.parse(str);
  return Number.isFinite(parsed) && !Number.isNaN(parsed) ? parsed : 0;
};

const sortSourceAssignmentsForActivityPicker = (
  list: SourceAssignment[],
  mode: SourceActivityListSort,
): SourceAssignment[] => {
  if (!Array.isArray(list) || list.length <= 1) {
    return [...list];
  }
  const titleKey = (a: SourceAssignment) =>
    String(a.title || get(a, 'displayName') || get(a, 'data.displayName') || '').toLowerCase();
  const tieId = (a: SourceAssignment) => String(a.id);
  return [...list].sort((a, b) => {
    if (mode === 'title_az') {
      const c = titleKey(a).localeCompare(titleKey(b));
      return c !== 0 ? c : tieId(a).localeCompare(tieId(b));
    }
    if (mode === 'title_za') {
      const c = titleKey(b).localeCompare(titleKey(a));
      return c !== 0 ? c : tieId(a).localeCompare(tieId(b));
    }
    const ma = getSourceAssignmentModifiedMs(a);
    const mb = getSourceAssignmentModifiedMs(b);
    if (mode === 'modified_asc') {
      return ma !== mb ? ma - mb : tieId(a).localeCompare(tieId(b));
    }
    return ma !== mb ? mb - ma : tieId(a).localeCompare(tieId(b));
  });
};

const SOURCE_ACTIVITY_SORT_TOOLTIPS: Record<SourceActivityListSort, string> = {
  title_az: 'Alphabetical order by activity or quiz title (A–Z)',
  title_za: 'Reverse alphabetical by title (Z–A)',
  modified_asc: 'Oldest first by last modified time',
  modified_desc: 'Newest first by last modified time',
};

const SOURCE_ACTIVITY_SORT_SELECT_OPTIONS: { value: SourceActivityListSort; label: string }[] = [
  { value: 'title_az', label: 'A–Z by title' },
  { value: 'title_za', label: 'Z–A by title' },
  { value: 'modified_asc', label: 'Oldest first' },
  { value: 'modified_desc', label: 'Newest first' },
];

const collectGroupAssignmentIds = (groups: SourceAssignmentGroup[]): Set<string> => {
  const ids = new Set<string>();
  const walk = (nodes: SourceAssignmentGroup[]) => {
    nodes.forEach((node) => {
      node.assignments.forEach((assignment) => ids.add(assignment.id));
      if (node.children.length > 0) {
        walk(node.children);
      }
    });
  };
  walk(groups);
  return ids;
};

const getFirstAssignmentPage = (assignment: SourceAssignment): number => {
  const firstPage = getAssignmentPageNumbers(assignment)[0];
  return typeof firstPage === 'number' && Number.isFinite(firstPage) ? firstPage : Number.MAX_SAFE_INTEGER;
};

const sortSourceAssignmentsByMaskNumber = (
  list: SourceAssignment[],
  allBooks: any[],
): SourceAssignment[] => {
  if (!Array.isArray(list) || list.length <= 1) {
    return [...list];
  }
  const books = Array.isArray(allBooks) ? allBooks : [];
  const maskSortValue = (assignment: SourceAssignment): number => {
    const chapterRaw = get(assignment, 'data.chapter') ?? get(assignment, 'chapter') ?? get(assignment, 'bookId');
    const chapter = chapterRaw != null && String(chapterRaw).trim() !== '' ? String(chapterRaw) : '';
    const firstPage = getFirstAssignmentPage(assignment);
    if (!chapter || firstPage === Number.MAX_SAFE_INTEGER) {
      return Number.MAX_SAFE_INTEGER;
    }
    const pageId = String(
      get(assignment, 'data.page') ??
        get(assignment, 'page') ??
        get(assignment, 'pageId') ??
        get(assignment, 'data.pageId') ??
        firstPage,
    );
    const mask = getNumberMask(chapter, pageId, books);
    if (mask == null || mask === '') {
      return firstPage;
    }
    const parsed = parseInt(String(mask).replace(/\D/g, ''), 10);
    return Number.isFinite(parsed) && !Number.isNaN(parsed) ? parsed : firstPage;
  };
  return [...list].sort((a, b) => {
    const va = maskSortValue(a);
    const vb = maskSortValue(b);
    if (va !== vb) {
      return va - vb;
    }
    return String(a.id).localeCompare(String(b.id));
  });
};

const buildEbookAssignmentGroups = (
  structures: any[],
  assignments: SourceAssignment[],
  prefix = 'unit',
  allBooks: any[] = [],
  listSortMode: SourceActivityListSort = 'title_az',
): SourceAssignmentGroup[] => {
  if (!Array.isArray(structures)) return [];

  const groups = structures
    .map((structure, index) => {
      const nestedStructures = [
        ...(Array.isArray(structure?.subUnits) ? structure.subUnits : []),
        ...(Array.isArray(structure?.subLessons) ? structure.subLessons : []),
      ];

      const matchingAssignments = assignments.filter((assignment) =>
        assignmentMatchesRange(assignment, structure?.start, structure?.finish),
      );

      const children = buildEbookAssignmentGroups(
        nestedStructures,
        matchingAssignments,
        `${prefix}-${structure?.id || index}`,
        allBooks,
        listSortMode,
      );

      const childAssignmentIds = collectGroupAssignmentIds(children);
      const ownAssignments = matchingAssignments.filter((assignment) => !childAssignmentIds.has(assignment.id));
      const ownAssignmentsSorted = sortSourceAssignmentsForActivityPicker(ownAssignments, listSortMode);

      let resolvedChildren = children;
      let resolvedAssignments: SourceAssignment[] = ownAssignmentsSorted;

      if (nestedStructures.length > 0 && ownAssignmentsSorted.length > 0) {
        const childPrefix = `${prefix}-${structure?.id || index}`;
        const childrenById = new Map(children.map((child) => [child.id, child]));
        const orderedChildren = nestedStructures
          .map((nestedStructure, nestedIndex) => {
            const nestedId = `${childPrefix}-${nestedStructure?.id || nestedStructure?.subTopicId || nestedStructure?.label || nestedIndex}`;
            return {
              start: toPageNumber(nestedStructure?.start),
              group: childrenById.get(nestedId) || null,
            };
          })
          .filter((entry) => entry.group !== null) as { start: number | null; group: SourceAssignmentGroup }[];

        const pendingOwnAssignments = [...ownAssignmentsSorted].sort((a, b) => getFirstAssignmentPage(a) - getFirstAssignmentPage(b));
        const distributedChildren: SourceAssignmentGroup[] = [];
        let coverIndex = 0;

        orderedChildren.forEach((entry) => {
          const coverAssignments: SourceAssignment[] = [];
          while (pendingOwnAssignments.length > 0) {
            const nextAssignment = pendingOwnAssignments[0];
            const nextPage = getFirstAssignmentPage(nextAssignment);
            if (entry.start !== null && nextPage >= entry.start) {
              break;
            }
            coverAssignments.push(pendingOwnAssignments.shift() as SourceAssignment);
          }

          if (coverAssignments.length > 0) {
            distributedChildren.push({
              id: `${childPrefix}-cover-${coverIndex}`,
              title: 'Cover',
              assignments: coverAssignments,
              children: [],
            });
            coverIndex += 1;
          }

          distributedChildren.push(entry.group);
        });

        if (pendingOwnAssignments.length > 0) {
          distributedChildren.push({
            id: `${childPrefix}-cover-${coverIndex}`,
            title: 'Cover',
            assignments: pendingOwnAssignments,
            children: [],
          });
        }

        if (distributedChildren.length > 0) {
          resolvedChildren = distributedChildren;
          resolvedAssignments = [];
        }
      }

      return {
        id: `${prefix}-${structure?.id || structure?.subTopicId || structure?.label || index}`,
        title: structure?.shortName || structure?.label || `Section ${index + 1}`,
        assignments: resolvedAssignments,
        children: resolvedChildren,
      };
    })
    .filter((group) => group.assignments.length > 0 || group.children.length > 0);

  const assignedIds = collectGroupAssignmentIds(groups);
  const ungroupedAssignments = assignments.filter((assignment) => !assignedIds.has(assignment.id));
  if (!ungroupedAssignments.length) {
    return groups;
  }

  const coverGroupIndex = groups.findIndex((group) => String(group.title || '').trim().toLowerCase() === 'cover');
  if (coverGroupIndex === -1) {
    return groups;
  }

  const coverGroup = groups[coverGroupIndex];
  const mergedCoverAssignments = sortSourceAssignmentsByMaskNumber(
    [...coverGroup.assignments, ...ungroupedAssignments],
    allBooks,
  );

  return groups.map((group, index) =>
    index === coverGroupIndex
      ? {
          ...group,
          assignments: mergedCoverAssignments,
        }
      : group,
  );
};

const AssignmentBuilderTool: React.FC<AssignmentBuilderToolProps> = ({
  open,
  onClose,
  data,
  from = 'ebook',
  page,
  chapter,
  pageSrc,
  assignmentRecord = null,
  disableSubmit = false,
  formResource = '',
  storyInstruction = '',
  article_level_id = '',
  onCreated,
  pageTitle,
  BALCON_PAGES = [],
  generatedTest = false,
}) => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const { showAlert, setAlertLoading } = useAlertLoading();
  const standards = useStandards();
  const enabledStateCodes = useOrgStandardCodes();
  const activities = useActivities();
  const allBooks = useAllBook();
  const quizzes = useQuizzes();
  const resources = useResources();
  const [questions, setQuestions] = useState<BuilderQuestion[]>([]);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [draft, setDraft] = useState<BuilderQuestion | null>(null);
  const [previewHtml, setPreviewHtml] = useState(false);
  const [assignmentTitle, setAssignmentTitle] = useState('');
  const [languageFeedback, setLanguageFeedback] = useState('SPANISH');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [usedAiQuestions, setUsedAiQuestions] = useState(false);
  const [assignmentAiModal, setAssignmentAiModal] = useState(false);
  const [assignmentAiSource, setAssignmentAiSource] = useState<BuilderAiSource | null>(null);
  const [aiSourcePickerOpen, setAiSourcePickerOpen] = useState(false);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [activityPickerOpen, setActivityPickerOpen] = useState(false);
  const [sourceAssignments, setSourceAssignments] = useState<SourceAssignment[]>([]);
  const [selectedSourceAssignmentId, setSelectedSourceAssignmentId] = useState<string>('');
  const [selectedImportQuestionKeys, setSelectedImportQuestionKeys] = useState<string[]>([]);
  const [newQuestionType, setNewQuestionType] = useState<string>('essay');
  const [sourceAssignmentsLoading, setSourceAssignmentsLoading] = useState(false);
  const [sourceAssignmentSearch, setSourceAssignmentSearch] = useState('');
  const [sourceActivityListSort, setSourceActivityListSort] = useState<SourceActivityListSort>('title_az');
  const [selectedSourceBookId, setSelectedSourceBookId] = useState<string>('');
  const [selectedSourceResourceType, setSelectedSourceResourceType] = useState<string>('');
  const [pagePreviewOpen, setPagePreviewOpen] = useState(false);
  const [questionPreviewData, setQuestionPreviewData] = useState<any | null>(null);
  const [coverPreviewSrc, setCoverPreviewSrc] = useState<string>('');
  const [coverPreviewOpen, setCoverPreviewOpen] = useState(false);
  const [toolsDrawerOpen, setToolsDrawerOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [superAdminActivity, setSuperAdminActivity] = useState<SuperAdminActivityState>(() => emptySuperAdminActivity());
  const dirtyBaselineRef = useRef<string | null>(null);
  const prevOpenRef = useRef(false);
  const editSectionRef = useRef<HTMLDivElement | null>(null);
  const openStateRef = useRef({
    assignmentTitle,
    languageFeedback,
    usedAiQuestions,
    questions,
    draft,
  });
  openStateRef.current = { assignmentTitle, languageFeedback, usedAiQuestions, questions, draft };
  const { maskId } = useGetMaskId(from, page, chapter);
  const theme = useTheme();
  const builderPortalsTheme = useMemo(
    () =>
      createTheme({
        ...theme,
        zIndex: {
          ...theme.zIndex,
          modal: Z_BUILDER_MUI_PORTALS,
        },
      }),
    [theme],
  );
  const builderDialogMenuTheme = useMemo(
    () =>
      createTheme({
        ...theme,
        zIndex: {
          ...theme.zIndex,
          modal: Z_BUILDER_DIALOG_MENU_THEME_MODAL,
        },
      }),
    [theme],
  );
  const dialogFullScreen = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const draftPreviewCompact = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const draftPreviewHeight = draftPreviewCompact ? 320 : 520;
  const compactBuilder = useMediaQuery('(max-width: 1100px)', { noSsr: true });

  const scheduleScrollToEditSection = useCallback(() => {
    if (!compactBuilder) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        editSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, [compactBuilder]);

  useEffect(() => {
    if (!compactBuilder) setToolsDrawerOpen(false);
  }, [compactBuilder]);

  useEffect(() => {
    if (!open) {
      setDiscardDialogOpen(false);
      dirtyBaselineRef.current = null;
      prevOpenRef.current = false;
      return;
    }
    if (!prevOpenRef.current) {
      const s = openStateRef.current;
      dirtyBaselineRef.current = buildAssignmentBuilderPersistSnapshot(
        s.assignmentTitle,
        s.languageFeedback,
        s.usedAiQuestions,
        s.questions,
        s.draft,
      );
    }
    prevOpenRef.current = true;
  }, [open]);

  const getBookData = useMemo(
    () => find(allBooks, (book: { id?: string | number }) => String(book?.id) === String(chapter)),
    [allBooks, chapter],
  );

  const isSuperAdmin = currentUser?.role === 'owe_admin';
  const story = useStory();
  const exams = useApExams();
  const bookUnitList = useBookUnits();
  const standardExams = useStandardExams();
  const isExamActive = useIsExamActive({ bookId: String(chapter) });
  const checkExamBookType = Boolean(isExamActive?.checkExamBookType);
  const isStandardExamBook = useMemo(() => checkStandardExam(chapter, standardExams), [chapter, standardExams]);
  const isStoryBook = Boolean(find(story, ['bookId', chapter]));
  const isApExams = Boolean(find(exams, (k) => find(k.exams, (c) => String(c.bookId) === String(chapter))));
  const bookPagesForBuilder = useMemo(() => (get(getBookData, 'pages', []) || []) as { pageId?: string | number; NumberMasked?: string }[], [getBookData]);
  const ebookGradeLevels = useMemo(() => get(getBookData, 'grade_levels', []) || [], [getBookData]);
  const matchedReadLessonPage = useMemo(() => {
    const data = find(bookUnitList, ['bookId', chapter]);
    if (!data) return null;
    const pages = get(data, 'pages', []);
    const current = Number(page);
    if (Number.isNaN(current)) return null;
    return (
      find(pages, (p: { start?: unknown; finish?: unknown }) => {
        const start = Number(p.start);
        const finish = Number(p.finish);
        return current >= start && current <= finish;
      }) || null
    );
  }, [bookUnitList, chapter, page]);
  const readLessonPagePool = useMemo(() => {
    if (!matchedReadLessonPage) return bookPagesForBuilder;
    const lo = Number(matchedReadLessonPage.start);
    const hi = Number(matchedReadLessonPage.finish);
    const filtered = bookPagesForBuilder.filter((p) => {
      const n = parseInt(String(p.pageId), 10);
      return n >= lo && n <= hi;
    });
    return filtered.length > 0 ? filtered : bookPagesForBuilder;
  }, [bookPagesForBuilder, matchedReadLessonPage]);
  const defaultPageSelection = useMemo(() => {
    const current = parseInt(String(page), 10);
    const exists = bookPagesForBuilder.some((p) => parseInt(String(p.pageId), 10) === current);
    if (exists) return current;
    const first = bookPagesForBuilder[0]?.pageId;
    return first != null ? parseInt(String(first), 10) : 1;
  }, [page, bookPagesForBuilder]);

  const patchSuperAdminActivity = useCallback((patch: Partial<SuperAdminActivityState>) => {
    setSuperAdminActivity((prev) => ({ ...prev, ...patch }));
  }, []);

  useEffect(() => {
    if (!open || !isSuperAdmin) return;
    if (assignmentRecord && has(assignmentRecord, 'id')) {
      setSuperAdminActivity({
        level: (get(assignmentRecord, 'level', null) as string | null) ?? null,
        storyType: String(get(assignmentRecord, 'type', '')) === 'story',
        apExamType: String(get(assignmentRecord, 'type', '')) === 'apexam',
        examCategories: String(get(assignmentRecord, 'category', '') ?? ''),
        assignmentStandards: (() => {
          const s = get(assignmentRecord, 'standards', []);
          return Array.isArray(s) ? s : s ? [s] : [];
        })(),
        isExam: Boolean(get(assignmentRecord, 'is_exam', false)),
        isUnitExam: Boolean(get(assignmentRecord, 'is_unit_exam', false)),
        unit: get(assignmentRecord, 'unit', null) ?? null,
        unit_exam: get(assignmentRecord, 'unit_exam', null) ?? null,
        rubrics_id: get(assignmentRecord, 'rubrics_id', null) ?? null,
        ai_grading_config: (get(assignmentRecord, 'ai_grading_config', {}) as Record<string, unknown>) || {},
        pageLinkConfiguration: Boolean(get(assignmentRecord, 'pageLinkConfiguration', false)),
      });
      return;
    }
    setSuperAdminActivity(emptySuperAdminActivity());
  }, [open, isSuperAdmin, assignmentRecord]);

  const bookContentData = useMemo(() => {
    if (data?.content && Array.isArray(data.content) && data.content.length > 0) {
      return { content: data.content };
    }
    return { content: [] };
  }, [data]);

  const aiSourcePickerDefaults = useMemo(() => {
    if (from === 'podcast' || formResource === 'resource') {
      const coverItem = imageFetchFromPodcast(chapter, resources)
      return {
        defaultResourceType: resource_types.resource,
        defaultSelectedItems: [{ id: chapter, displayName: pageTitle || String(chapter), src:coverItem , thumbnail:coverItem   }],
        defaultSelectedConfig: PICKER_EMPTY_CONFIG,
      };
    }
    if (from === resource_types.balcon || from === resource_types.conectándome || from === resource_types.comunidad || from === resource_types.informate || from === resource_types.story || from === resource_types.AP) {
      const p = parseInt(String(page), 10);
      let items: unknown[] = PICKER_EMPTY_ITEMS;
      if (Array.isArray(BALCON_PAGES) && BALCON_PAGES.length > 0) {
        const unit = BALCON_PAGES.find((u: { start?: unknown; finish?: unknown; shortName?: string }) => {
          const s = parseInt(String(u.start), 10);
          const f = parseInt(String(u.finish), 10);
          return Number.isFinite(p) && Number.isFinite(s) && Number.isFinite(f) && p >= s && p <= f;
        });
        if (unit) {
          items = [
            {
              ...unit,
              start: unit.start,
              finish: unit.finish,
              shortName: unit.shortName || pageTitle || '',
            },
          ];
        }
      }
      return {
        defaultResourceType: from,
        defaultSelectedItems: items,
        defaultSelectedConfig: PICKER_EMPTY_CONFIG,
      };
    }
    return {
      defaultResourceType: 'ebook',
      defaultSelectedItems: PICKER_EMPTY_ITEMS,
      defaultSelectedConfig: PICKER_EMPTY_CONFIG,
    };
  }, [from, formResource, chapter, page, pageTitle, BALCON_PAGES]);

  const selected = useMemo(
    () => questions.find((q) => q._uid === selectedUid) ?? null,
    [questions, selectedUid],
  );

  const selectedQuestionOrdinal = useMemo(() => {
    if (!selectedUid) return null;
    const idx = questions.findIndex((q) => q._uid === selectedUid);
    return idx >= 0 ? idx + 1 : null;
  }, [questions, selectedUid]);

  const draftPreviewData = useMemo(() => {
    if (!draft) return null;
    const draftQuestions =
      Array.isArray(draft.subQuestions) && draft.subQuestions.length
        ? draft.subQuestions.map((item) => {
            const { _uid, subQuestions, ...rest } = item as any;
            return rest;
          })
        : [draft].map((item) => {
            const { _uid, subQuestions, ...rest } = item as any;
            return rest;
          });

    return {
      displayName: assignmentTitle || 'Question Preview',
      questions: draftQuestions,
    };
  }, [assignmentTitle, draft]);

  const selectQuestion = useCallback(
    (uid: string) => {
      setSelectedUid(uid);
      const q = questions.find((x) => x._uid === uid);
      setDraft(q ? inflateDraft(q, questions) : null);
      scheduleScrollToEditSection();
    },
    [questions, scheduleScrollToEditSection],
  );

  const handleAddQuestion = useCallback(() => {
    const id = getNextId(questions);
    const q = defaultQuestionForType(newQuestionType, id);
    const next = [...questions, q];
    setQuestions(next);
    setSelectedUid(q._uid);
    setDraft(cloneDeep(q));
    scheduleScrollToEditSection();
  }, [questions, newQuestionType, scheduleScrollToEditSection]);

  const handleDelete = useCallback(
    (uid: string) => {
      setQuestions((prev) => {
        const next = prev.filter((q) => q._uid !== uid);
        const shouldResetSelection = selectedUid === uid;
        if (shouldResetSelection) {
          const fallback = next[next.length - 1];
          setSelectedUid(fallback?._uid ?? null);
          setDraft(fallback ? inflateDraft(fallback, next) : null);
          if (fallback) scheduleScrollToEditSection();
        } else if (draft?._uid) {
          const refreshedDraft = next.find((q) => q._uid === draft._uid);
          if (refreshedDraft) {
            setDraft(inflateDraft(refreshedDraft, next));
          }
        }
        return next;
      });
    },
    [draft?._uid, selectedUid, scheduleScrollToEditSection],
  );

  const setDraftFromBuilder = useCallback<React.Dispatch<React.SetStateAction<BuilderQuestion | null>>>(
    (action) => {
      setDraft((prev) => {
        const next =
          typeof action === 'function'
            ? (action as (p: BuilderQuestion | null) => BuilderQuestion | null)(prev)
            : action;
        if (next) {
          setQuestions((qPrev) => applyDraftToQuestions(qPrev, next));
        }
        return next;
      });
    },
    [],
  );

  const handleUpdateDraft = useCallback(
    (patch: Partial<BuilderQuestion>) => {
      setDraft((d) => {
        if (!d) return null;
        const next = { ...d, ...patch };
        setQuestions((prev) => applyDraftToQuestions(prev, next));
        return next;
      });
    },
    [],
  );

  const handleUpdate = useCallback(() => {
    if (!draft) return;
    const merged = applyDraftToQuestions(questions, draft);
    const cleanForValidation = (q: BuilderQuestion) => {
      const { _uid, subQuestions, ...rest } = q as BuilderQuestion & { subQuestions?: unknown };
      return rest;
    };
    const uid = selectedUid || draft._uid;
    const row = merged.find((q) => q._uid === uid);
    if (!row) {
      showAlert('Could not validate this question.', 'error');
      return;
    }
    if (isStandardExamBook && String((row as Record<string, unknown>).explanation ?? '').trim() === '') {
      showAlert('Explanation is required for standard exam questions.', 'error');
      return;
    }
    const slice = [cleanForValidation(row)];
    const groupAudioMc =
      row.type === 'audioMultipleChoice' && row.groupKey
        ? merged.filter((q) => q.type === 'audioMultipleChoice' && q.groupKey === row.groupKey)
        : [];
    const audioMultipleChoicePartialIsGroupFirst =
      groupAudioMc.length > 0 ? groupAudioMc[0]._uid === row._uid : undefined;
    const validationErrors = collectAssignmentValidationErrors({
      displayName: ' ',
      languageFeedback: languageFeedback || 'SPANISH',
      questions: slice as any[],
      skipAssignmentFields: true,
      audioMultipleChoicePartialIsGroupFirst,
    });
    if (validationErrors.length > 0) {
      const msgs = formatAssignmentValidationMessages(validationErrors);
      showAlert(msgs.join('\n') || 'This question is incomplete.', 'error');
      return;
    }
    setQuestions((prev) => applyDraftToQuestions(prev, draft));
    if (isGroupedQuestionType(draft.type) && Array.isArray(draft.subQuestions) && draft.subQuestions[0]?._uid) {
      setSelectedUid(draft.subQuestions[0]._uid);
    }
    showAlert('Updated successfully.', 'success');
  }, [draft, isStandardExamBook, languageFeedback, questions, selectedUid, showAlert]);

  const handleAiResourceSelect = useCallback(
    (loc: { bookId: string; resourceType: string; items: any[]; selectedConfig: Record<string, unknown> }) => {
      const resolved = resolveBuilderAiSource(loc, allBooks, page, BALCON_PAGES);
      setAssignmentAiSource(resolved);
      setAssignmentAiModal(true);
      setAiSourcePickerOpen(false);
    },
    [allBooks, page, BALCON_PAGES],
  );

  const handleAiAdd = useCallback((aiQuestions: BuilderQuestion[]) => {
    setAssignmentAiModal(false);
    setAssignmentAiSource(null);
    setUsedAiQuestions(true);
    const withUids = ensureQuestionUids(aiQuestions as BuilderQuestion[]);
    setQuestions((prev) => {
      const next = [...prev, ...withUids];
      const last = withUids[withUids.length - 1];
      if (last) {
        setSelectedUid(last._uid);
        setDraft(cloneDeep(last));
      }
      return next;
    });
    scheduleScrollToEditSection();
  }, [scheduleScrollToEditSection]);

  const selectedSourceAssignment = useMemo(
    () => sourceAssignments.find((item) => item.id === selectedSourceAssignmentId) || null,
    [sourceAssignments, selectedSourceAssignmentId],
  );

  const filteredSourceAssignments = useMemo(() => {
    const searchValue = sourceAssignmentSearch.trim().toLowerCase();
    const searched = !searchValue
      ? [...sourceAssignments]
      : sourceAssignments.filter((item) => String(item.title || '').toLowerCase().includes(searchValue));
    return sortSourceAssignmentsForActivityPicker(searched, sourceActivityListSort);
  }, [sourceActivityListSort, sourceAssignmentSearch, sourceAssignments]);

  const ebookSourceAssignmentGroups = useMemo(() => {
    if (selectedSourceResourceType !== 'ebook' || !selectedSourceBookId) return [];
    const ebookData = ebookConfigDinamic(selectedSourceBookId, allBooks);
    return buildEbookAssignmentGroups(
      ebookData?.structures || [],
      filteredSourceAssignments,
      'unit',
      allBooks,
      sourceActivityListSort,
    );
  }, [allBooks, filteredSourceAssignments, selectedSourceBookId, selectedSourceResourceType, sourceActivityListSort]);

  const renderSourceAssignmentButton = useCallback(
    (assignment: SourceAssignment) => {
      const assignmentChapter = String(
        get(assignment, 'chapter') ||
          get(assignment, 'bookId') ||
          get(assignment, 'data.chapter') ||
          get(assignment, 'data.bookId') ||
          '',
      );
      const assignmentPages = getAssignmentPageNumbers(assignment);
      const assignmentPage = assignmentPages[0];
      const assignmentMaskNumber =
        assignmentChapter && assignmentPage !== undefined
          ? getNumberMask(assignmentChapter, assignmentPage, allBooks)
          : null;
      const assignmentPageCover = assignmentPage !== undefined ? imageFetch(allBooks, assignmentChapter, assignmentPage) : '';
      const modalSrc =
        assignmentPageCover ||
        (assignmentChapter && isVideo(assignmentChapter, resources) ? imageFetchFromPodcast(assignmentChapter, resources) : '');
      const isUnitExam = Boolean(get(assignment, 'is_unit_exam') || get(assignment, 'data.is_unit_exam'));
      const isExamAssignment =
        Boolean(get(assignment, 'is_exam') || get(assignment, 'data.is_exam')) ||
        Boolean(allBooks.find((book: any) => String(book?.id) === assignmentChapter)?.quizAssessment);

      return (
        <button
          key={assignment.id}
          type="button"
          className={`${styles.selectionItem} ${selectedSourceAssignmentId === assignment.id ? styles.selectionItemActive : ''}`}
          onClick={() => setSelectedSourceAssignmentId(assignment.id)}
        >
          <div className={styles.selectionItemBody}>
            <div className={styles.selectionItemMain}>
              <div className={styles.selectionItemHead}>
                {(isUnitExam || isExamAssignment) && (
                  <span className={`${styles.selectionItemBadge} ${isUnitExam ? styles.selectionItemBadgeUnit : styles.selectionItemBadgeExam}`}>
                    {isUnitExam ? 'UNIT EXAM' : 'EXAMEN'}
                  </span>
                )}
              </div>
              <div className={styles.selectionItemTitle}>{assignment.title}</div>
              <div className={styles.selectionItemMeta}>{assignment.questions.length} questions</div>
            </div>

            <div className={styles.selectionItemAside}>
              {assignmentMaskNumber ? <div className={styles.selectionItemCoverMask}>Pg. {assignmentMaskNumber}</div> : null}
              {modalSrc ? (
                <button
                  type="button"
                  className={styles.selectionItemThumbBtn}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setCoverPreviewSrc(modalSrc);
                    setCoverPreviewOpen(true);
                  }}
                  aria-label="zoom cover"
                >
                  <SourceAssignmentCoverThumb modalSrc={modalSrc} />
                </button>
              ) : null}
            </div>
          </div>
        </button>
      );
    },
    [allBooks, selectedSourceAssignmentId],
  );

  const renderSourceAssignmentGroup = useCallback(
    (group: SourceAssignmentGroup, depth = 0): React.ReactNode => (
      <Accordion
        key={group.id}
        disableGutters
        defaultExpanded={false}
        className={`${styles.sourceAccordion} ${depth > 0 ? styles.sourceAccordionNested : ''}`}
      >
        <AccordionSummary expandIcon={<ExpandMore fontSize="small" />}>
          <div className={styles.sourceAccordionTitleWrap}>
            <div className={styles.sourceAccordionTitle}>{group.title}</div>
            <div className={styles.sourceAccordionMeta}>
              {group.assignments.length + group.children.reduce((sum, child) => sum + child.assignments.length, 0)} Activities
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails className={styles.sourceAccordionDetails}>
          {group.assignments.map((assignment) => renderSourceAssignmentButton(assignment))}
          {group.children.map((childGroup) => renderSourceAssignmentGroup(childGroup, depth + 1))}
        </AccordionDetails>
      </Accordion>
    ),
    [renderSourceAssignmentButton],
  );

  const handleSourceLocationSelect = useCallback(
    async ({ bookId: selectedBookId , items:itemRefrence , selectedConfig:selectedConfigRefrence, resourceType }) => {
      const normalizeApiItems = (response: any) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        return [];
      };

      const getSelectedPageRange = () => {
        const firstItem = Array.isArray(itemRefrence) ? itemRefrence[0] : null;
        if (!firstItem) return null;

        const selectedLevel = String(get(selectedConfigRefrence, 'level', '')).toLowerCase();
        const itemLevels = Array.isArray(firstItem?.levels) ? firstItem.levels : [];

        if (selectedLevel && itemLevels.length > 0) {
          const matchedLevel = itemLevels.find((levelItem: any) => String(levelItem?.level || '').toLowerCase() === selectedLevel);
          const levelStart = toPageNumber(matchedLevel?.start);
          const levelFinish = toPageNumber(matchedLevel?.finish);
          if (levelStart !== null && levelFinish !== null) {
            return { start: levelStart, finish: levelFinish };
          }
        }

        const itemStart = toPageNumber(firstItem?.start);
        const itemFinish = toPageNumber(firstItem?.finish);
        if (itemStart !== null && itemFinish !== null) {
          return { start: itemStart, finish: itemFinish };
        }

        return null;
      };

      const pageRange = getSelectedPageRange();

  

      const getEndpointWithRange = (basePath: string) => {
        if (!pageRange) return basePath;
        const params = new URLSearchParams({
          start: String(pageRange.start),
          finish: String(pageRange.finish),
        });
        return `${basePath}?${params.toString()}`;
      };

      const isGraphicOrganizerAssignment = (item: any) =>
        get(item, 'assignment_type') === 'graphic_organizer' ||
        get(item, 'data.assignment_type') === 'graphic_organizer' ||
        get(item, 'activity_type') === 'graphic_organizer' ||
        get(item, 'data.activity_type') === 'graphic_organizer';

      const mergeUniqueAssignments = (primaryItems: any[], secondaryItems: any[]) => {
        const mergedMap = new Map();

        [...(Array.isArray(primaryItems) ? primaryItems : []), ...(Array.isArray(secondaryItems) ? secondaryItems : [])].forEach((item, index) => {
          const itemId = String(get(item, 'id') || get(item, 'data.id') || `merged-${index}`);
          if (!mergedMap.has(itemId)) {
            mergedMap.set(itemId, item);
          }
        });

        return Array.from(mergedMap.values());
      };

      const normalizeAssignments = (items: any[], fallbackType: string) =>
        (Array.isArray(items) ? items : [])
          .filter((item) => {
            const itemBookId = get(item, 'bookId') || get(item, 'chapter') || get(item, 'data.bookId') || get(item, 'data.chapter');
            return String(itemBookId) === String(selectedBookId) && !isGraphicOrganizerAssignment(item);
          })
          .map((item, index) => {
            const itemId = String(get(item, 'id') || get(item, 'data.id') || `${fallbackType}-${index}`);
            const itemQuestions = get(item, 'questions') || get(item, 'data.questions') || [];
            return {
              id: `${fallbackType}-${itemId}`,
              title: get(item, 'displayName') || get(item, 'data.displayName') || `${fallbackType === 'quiz' ? 'Quiz' : 'Activity'} ${index + 1}`,
              sourceType: get(item, 'typeId') || get(item, 'data.typeId') || fallbackType,
              questions: Array.isArray(itemQuestions) ? itemQuestions : [],
              ...item,
            };
          })
          .filter((item) => item &&Array.isArray(item.questions) && item.questions.length > 0);

      setSelectedImportQuestionKeys([]);
      setSelectedSourceAssignmentId('');
      setSourceAssignments([]);
      setSourceAssignmentSearch('');
      setSourceActivityListSort('title_az');
      setSelectedSourceBookId(String(selectedBookId));
      setSelectedSourceResourceType(String(resourceType || ''));
      setActivityPickerOpen(true);
      setSourceAssignmentsLoading(true);

      try {
        const [activityResponse, quizResponse] = await Promise.all([
          apis.get('activitiesRDS', getEndpointWithRange(`/activitiesByChapter/${selectedBookId}`)),
          apis.get('quizzesRDS', getEndpointWithRange(`/quizzesByChapter/${selectedBookId}`)),
        ]);

        const mergedActivities = mergeUniqueAssignments(normalizeApiItems(activityResponse), activities);
        const mergedQuizzes = mergeUniqueAssignments(normalizeApiItems(quizResponse), quizzes);

        const nextAssignments = [
          ...normalizeAssignments(mergedActivities, 'activity'),
          ...normalizeAssignments(mergedQuizzes, 'quiz'),
        ];
        setSourceAssignments(nextAssignments);
        setSelectedSourceAssignmentId('');
      } catch (error) {
        const fallbackAssignments = [
          ...normalizeAssignments(activities, 'activity'),
          ...normalizeAssignments(quizzes, 'quiz'),
        ];

        setSourceAssignments(fallbackAssignments);
        setSelectedSourceAssignmentId('');
        showAlert('Unable to load latest activities and quizzes. Showing available data.', 'error');
      } finally {
        setSourceAssignmentsLoading(false);
      }

      setSelectedImportQuestionKeys([]);
    },
    [activities, quizzes, showAlert],
  );

  const handleToggleImportQuestion = useCallback((questionKey: string) => {
    setSelectedImportQuestionKeys((prev) =>
      prev.includes(questionKey) ? prev.filter((item) => item !== questionKey) : [...prev, questionKey],
    );
  }, []);

  const handleOpenQuestionPreview = useCallback((question: BuilderQuestion, index: number) => {
    setQuestionPreviewData({
      displayName: `Question ${index + 1} Preview`,
      questions: [cloneDeep(question)],
    });
  }, []);

  const handleImportSelectedQuestions = useCallback(() => {
    const selectedQuestions = sourceAssignments.flatMap((assignment) =>
      assignment.questions
        .map((question, index) => ({
          question,
          key: `${assignment.id}-${index}`,
        }))
        .filter((item) => selectedImportQuestionKeys.includes(item.key))
    );

    const importedQuestions = selectedQuestions.map((item, index) => ({
      ...cloneDeep(item.question),
      id: getNextId(questions) + index,
      _uid: `q-${Math.random().toString(36).slice(2)}`,
    }));

    if (!importedQuestions.length) return;

    setQuestions((prev) => {
      const next = [...prev, ...importedQuestions];
      const last = importedQuestions[importedQuestions.length - 1];
      if (last) {
        setSelectedUid(last._uid);
        setDraft(cloneDeep(last));
      }
      return next;
    });
    setSelectedImportQuestionKeys([]);
    setActivityPickerOpen(false);
    scheduleScrollToEditSection();
  }, [questions, selectedImportQuestionKeys, sourceAssignments, scheduleScrollToEditSection]);

  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;
      if (!active || !over || active.id === over.id) return;

      setQuestions((prev) => {
        const oldIndex = prev.findIndex((q) => q._uid === active.id);
        const newIndex = prev.findIndex((q) => q._uid === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;
        return arrayMove(prev, oldIndex, newIndex).map((q, index) => ({
          ...q,
          id: index + 1,
        }));
      });

      if (draft?._uid) {
        setDraft((prev) => (prev ? { ...prev } : prev));
      }
    },
    [draft?._uid],
  );

  const resetBuilderUiState = useCallback(() => {
    setQuestions([]);
    setSelectedUid(null);
    setDraft(null);
    setPreviewHtml(false);
    setAssignmentTitle('');
    setLanguageFeedback('SPANISH');
    setUsedAiQuestions(false);
    setAssignmentAiModal(false);
    setAssignmentAiSource(null);
    setAiSourcePickerOpen(false);
    setSourcePickerOpen(false);
    setActivityPickerOpen(false);
    setSourceAssignments([]);
    setSelectedSourceAssignmentId('');
    setSelectedImportQuestionKeys([]);
    setNewQuestionType('essay');
    setSourceAssignmentsLoading(false);
    setSourceAssignmentSearch('');
    setSourceActivityListSort('title_az');
    setSelectedSourceBookId('');
    setSelectedSourceResourceType('');
    setToolsDrawerOpen(false);
    setSuperAdminActivity(emptySuperAdminActivity());
  }, []);

  const closeToolsDrawer = useCallback(() => setToolsDrawerOpen(false), []);

  const handleCreateAssignment = useCallback(async () => {
    if (disableSubmit || submitLoading) return;
    if (!currentUser?.id) {
      showAlert('You must be signed in to save an assignment.', 'error');
      return;
    }
    const preparedQuestions = applyDraftToQuestions(questions, draft);
    let cleaned = preparedQuestions.map((q) => {
      const { _uid, subQuestions, ...rest } = q as BuilderQuestion & { subQuestions?: unknown };
      return rest;
    }) as BuilderQuestion[];

    if (isSuperAdmin) {
      cleaned = cleaned.map((q) => {
        if (q.type === 'essay' && Array.isArray((q as Record<string, unknown>).expected_answer)) {
          const arr = ((q as Record<string, unknown>).expected_answer as string[]).filter(
            (ans) => typeof ans === 'string' && ans.replace(/<[^>]*>/g, '').trim().length > 0,
          );
          return { ...q, expected_answer: arr };
        }
        return q;
      });
    }
    if (isStandardExamBook) {
      const missingExplanationIndex = cleaned.findIndex(
        (q) => String((q as Record<string, unknown>).explanation ?? '').trim() === '',
      );
      if (missingExplanationIndex !== -1) {
        showAlert(`Explanation is required for question ${missingExplanationIndex + 1}.`, 'error');
        return;
      }
    }

    const validationErrors = collectAssignmentValidationErrors({
      displayName: assignmentTitle.trim(),
      languageFeedback,
      questions: cleaned as any[],
    });
    if (validationErrors.length > 0) {
      const msgs = formatAssignmentValidationMessages(validationErrors);
      showAlert(msgs.join('\n') || 'Validation failed', 'error');
      return;
    }

    if (isSuperAdmin) {
      if (isStoryBook && !superAdminActivity.storyType) {
        showAlert('Assignment for Story must be enabled for this book.', 'error');
        return;
      }
      if (isApExams && !superAdminActivity.apExamType) {
        showAlert('Assignment for AP must be enabled for this book.', 'error');
        return;
      }
      if (isApExams && !superAdminActivity.examCategories) {
        showAlert('Category for AP Exams is required.', 'error');
        return;
      }
      if (isStoryBook && !superAdminActivity.level) {
        showAlert('Assignment level is required for story books.', 'error');
        return;
      }
      if (superAdminActivity.isUnitExam && (!superAdminActivity.unit || !superAdminActivity.unit_exam)) {
        showAlert('Unit and unit exam are both required for unit exams.', 'error');
        return;
      }
      if (checkExamBookType && !superAdminActivity.examCategories) {
        showAlert('Category for Exams is required.', 'error');
        return;
      }
    }

    const isEditing = has(assignmentRecord, 'id');
    const assignmentData: Record<string, unknown> = {
      ...(assignmentRecord || {}),
      chapter: String(chapter),
      bookId: String(chapter),
      displayName: assignmentTitle,
      page,
      questions: cleaned,
      typeId: isEditing && assignmentRecord?.typeId ? assignmentRecord.typeId : 'activity',
      createdBy: get(currentUser, 'id'),
      language_feedback: languageFeedback,
      create_new_copy: false,
      is_ai: usedAiQuestions,
      ai_grading_config: isSuperAdmin
        ? mergeAiGradingConfigForPayload(
            superAdminActivity.ai_grading_config,
            superAdminActivity.pageLinkConfiguration,
            defaultPageSelection,
            bookPagesForBuilder,
            matchedReadLessonPage,
            readLessonPagePool,
          )
        : {},
      storyInstruction,
      article_level_id,
      resource: formResource === 'resource',
      ...(!isEditing ? { generated_test: Boolean(generatedTest) } : {}),
    };
    if (isSuperAdmin) {
      assignmentData.level = superAdminActivity.level ?? '';
      assignmentData.type = superAdminActivity.storyType ? 'story' : superAdminActivity.apExamType ? 'apexam' : '';
      assignmentData.category = superAdminActivity.examCategories;
      assignmentData.standards =
        superAdminActivity.assignmentStandards.length > 0 ? superAdminActivity.assignmentStandards : [];
      assignmentData.is_exam = superAdminActivity.isExam;
      assignmentData.is_unit_exam = superAdminActivity.isUnitExam;
      assignmentData.unit = superAdminActivity.isUnitExam ? superAdminActivity.unit ?? '' : '';
      assignmentData.unit_exam = superAdminActivity.isUnitExam ? superAdminActivity.unit_exam ?? '' : '';
      assignmentData.rubrics_id = superAdminActivity.rubrics_id;
      assignmentData.pageLinkConfiguration = superAdminActivity.pageLinkConfiguration;
      assignmentData.config_assignment = get(assignmentRecord, 'config_assignment', {}) || {};
    }

    try {
      setSubmitLoading(true);
      let assignmentPayload = cloneDeep(assignmentData);
      const ebookGradeLevels = get(getBookData, 'grade_levels', []) || [];
      const hasEbookStandards = Array.isArray(ebookGradeLevels) && ebookGradeLevels.length > 0;
      const questionsList = (assignmentPayload.questions || []) as BuilderQuestion[];
      const questionsWithStandards = questionsList.filter((q) => {
        const ss = q.state_standard;
        if (!ss || !Array.isArray(ss) || ss.length === 0) return false;
        return ss.some((stateStd: { standard?: unknown }) => {
          const standardArray = Array.isArray(stateStd?.standard)
            ? stateStd.standard
            : stateStd?.standard
              ? [stateStd.standard]
              : [];
          return Array.isArray(standardArray) && standardArray.length > 0;
        });
      });
      const questionsWithoutStandards = questionsList.filter((q) => {
        const ss = q.state_standard;
        if (!ss || !Array.isArray(ss) || ss.length === 0) return true;
        return !ss.some((stateStd: { standard?: unknown }) => {
          const standardArray = Array.isArray(stateStd?.standard)
            ? stateStd.standard
            : stateStd?.standard
              ? [stateStd.standard]
              : [];
          return Array.isArray(standardArray) && standardArray.length > 0;
        });
      });
      const shouldCallAutoSelectAPI =
        hasEbookStandards &&
        questionsWithoutStandards.length > 0 &&
        (!isEditing || (isEditing && questionsWithStandards.length > 0));

      if (shouldCallAutoSelectAPI) {
        try {
          setAlertLoading(true, 'Auto-selecting standards for questions...');
          const questionsWithoutStandardsIndices = questionsList
            .map((q, index) => {
              const hasStandards =
                q.state_standard &&
                Array.isArray(q.state_standard) &&
                q.state_standard.length > 0 &&
                q.state_standard.some((stateStd: { standard?: unknown }) => {
                  const standardArray = Array.isArray(stateStd?.standard)
                    ? stateStd.standard
                    : stateStd?.standard
                      ? [stateStd.standard]
                      : [];
                  return Array.isArray(standardArray) && standardArray.length > 0;
                });
              return hasStandards ? null : index;
            })
            .filter((index): index is number => index !== null);

          const questionsPayload = questionsWithoutStandardsIndices.map((originalIndex) => {
            const q = questionsList[originalIndex];
            return {
              type: q.type,
              id: q.id ?? originalIndex + 1,
              expected_answer: (q as { expected_answer?: unknown }).expected_answer || [],
              question: q.question || '',
            };
          });

          const enabledStateNames = enabledStateCodes.map((code: string) => normalizeStateForComparison(code)).filter(Boolean);
          const commonCoreStateName = normalizeStateForComparison('COMMONCORE');

          const filteredGradeLevels = ebookGradeLevels.filter((gradeLevelItem: { state?: string }) => {
            const stateCode = get(gradeLevelItem, 'state', '');
            if (!stateCode) return false;
            const stateNameNormalized = normalizeStateForComparison(stateCode);
            if (stateNameNormalized === commonCoreStateName) return true;
            if (enabledStateNames.length === 0) return false;
            return enabledStateNames.some((enabledName: string) => statesMatch(stateCode, enabledName));
          });

          const result = await dispatch(
            handleAutoSelectStandardsForAllQuestions({
              questions: questionsPayload,
              bookId: String(chapter),
              pageId: page ? parseInt(String(page), 10) : null,
              gradeLevels: filteredGradeLevels,
            }) as any,
          );

          if (result && Array.isArray(result) && result.length > 0) {
            const updatedQuestions = cloneDeep(questionsList);
            result.forEach((item: { questionIndex?: number; state_standard?: unknown; standards?: unknown[]; state?: string; grade?: unknown }, resultIndex: number) => {
              const originalQuestionIndex =
                questionsWithoutStandardsIndices[item.questionIndex !== undefined ? item.questionIndex : resultIndex];
              if (originalQuestionIndex !== undefined && updatedQuestions[originalQuestionIndex]) {
                if (item.state_standard && Array.isArray(item.state_standard)) {
                  (updatedQuestions[originalQuestionIndex] as { state_standard?: unknown }).state_standard = item.state_standard;
                } else if (item.standards && Array.isArray(item.standards) && item.standards.length > 0) {
                  const validStandards = item.standards.filter((abbr: string) => find(standards, (st) => st.abbr === abbr) !== undefined);
                  if (validStandards.length > 0 && item.state && item.grade !== undefined) {
                    (updatedQuestions[originalQuestionIndex] as { state_standard?: unknown }).state_standard = [
                      {
                        grade: item.grade,
                        standard: validStandards,
                        standard_name: item.state,
                      },
                    ];
                  }
                }
              }
            });
            assignmentPayload.questions = updatedQuestions;
          }
          setAlertLoading(false, '');
        } catch {
          setAlertLoading(false, '');
        }
      }

      if (isEditing && assignmentRecord?.id) {
        if (isSuperAdmin) {
          await dispatch(handleUpdateActivities(String(assignmentRecord.id), assignmentPayload as any) as any);
        } else {
          const updated = await dispatch(handleUpdateCustomAssignment(assignmentPayload as any, false, false) as any);
          if (!updated || !has(updated, 'id')) {
            showAlert('Could not update assignment. Please try again.', 'error');
            return;
          }
        }
        showAlert('Assignment updated', 'success');
      } else {
        if (isSuperAdmin) {
          await dispatch(handleCreateActivities(assignmentPayload as any) as any);
        } else {
          const created = await dispatch(handleCreateCustomAssignment(assignmentPayload as any) as any);
          if (!created || !has(created, 'id')) {
            showAlert('Could not create assignment. Please try again.', 'error');
            return;
          }
        }
        showAlert('Assignment created', 'success');
      }

      resetBuilderUiState();
      onClose();
      onCreated?.();
    } catch {
      showAlert('Could not save assignment. Please try again.', 'error');
    } finally {
      setSubmitLoading(false);
    }
  }, [
    assignmentRecord,
    assignmentTitle,
    chapter,
    currentUser,
    disableSubmit,
    dispatch,
    draft,
    enabledStateCodes,
    formResource,
    getBookData,
    languageFeedback,
    onClose,
    onCreated,
    page,
    questions,
    resetBuilderUiState,
    showAlert,
    storyInstruction,
    article_level_id,
    submitLoading,
    usedAiQuestions,
    generatedTest,
    isSuperAdmin,
    superAdminActivity,
    defaultPageSelection,
    bookPagesForBuilder,
    matchedReadLessonPage,
    readLessonPagePool,
    isStoryBook,
    isApExams,
    checkExamBookType,
    standards,
  ]);

  const finalizeBuilderClose = useCallback(() => {
    resetBuilderUiState();
    onClose();
  }, [onClose, resetBuilderUiState]);

  const handleClose = useCallback(() => {
    const base = dirtyBaselineRef.current;
    if (base !== null) {
      const now = buildAssignmentBuilderPersistSnapshot(
        assignmentTitle,
        languageFeedback,
        usedAiQuestions,
        questions,
        draft,
      );
      if (now !== base) {
        setDiscardDialogOpen(true);
        return;
      }
    }
    finalizeBuilderClose();
  }, [
    assignmentTitle,
    draft,
    finalizeBuilderClose,
    languageFeedback,
    questions,
    usedAiQuestions,
  ]);

  const handleDiscardDialogConfirm = useCallback(() => {
    setDiscardDialogOpen(false);
    finalizeBuilderClose();
  }, [finalizeBuilderClose]);

  const handleDiscardDialogCancel = useCallback(() => {
    setDiscardDialogOpen(false);
  }, []);

  const sidebarToolsNode = (
    <>
      <button
        type="button"
        className={styles.btnGhost}
        onClick={() => {
          setSourcePickerOpen(true);
          closeToolsDrawer();
        }}
      >
        <LibraryBooksOutlined sx={{ fontSize: 18 }} />
        Select Questions From Assignments
      </button>
      <button
        type="button"
        className={styles.btnAi}
        onClick={() => {
          setAiSourcePickerOpen(true);
          closeToolsDrawer();
        }}
      >
        <AutoAwesome sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 18 }} />
        Add AI Question
      </button>
      <div className={styles.addQuestionSection}>
        <h3>Manual question type</h3>
        <select
          className={styles.select}
          value={newQuestionType}
          onChange={(e) => setNewQuestionType(e.target.value)}
        >
          {QUESTION_TYPE_ACTIVITY_NON_NATIVE.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.type}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={() => {
            handleAddQuestion();
            closeToolsDrawer();
          }}
        >
          <AddCircleOutline sx={{ fontSize: 18 }} />
          Add Manual Question
        </button>
      </div>
    </>
  );

  return (
    <ThemeProvider theme={builderPortalsTheme}>
      <Modal
        open={open}
        onClose={handleClose}
        disableEnforceFocus={toolsDrawerOpen}
        sx={{
          zIndex: Z_BUILDER_SHELL,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'stretch',
          '& .MuiBackdrop-root': {
            touchAction: 'none',
          },
        }}
      >
        <div
          className={styles.overlay}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            maxHeight: '100dvh',
            outline: 'none',
          }}
        >
          <button
            type="button"
            className={styles.overlayCloseBtn}
            onClick={handleClose}
            aria-label="Close assignment builder"
          >
            <Close className={styles.overlayCloseIcon} />
          </button>
          <header className={styles.header}>
            <div className={styles.headerTop}>
              {compactBuilder ? (
                <IconButton
                  type="button"
                  onClick={() => setToolsDrawerOpen(true)}
                  aria-label="Open assignment tools"
                  size="small"
                  sx={{ flexShrink: 0, mr: 0.5, color: '#334155' }}
                >
                  <MenuIcon />
                </IconButton>
              ) : null}
              <div className={styles.headerInfo}>
                <span className={styles.title}>Assignment</span>
                {pageSrc ? (
                  <button
                    type="button"
                    className={styles.pagePreviewCard}
                    onClick={() => setPagePreviewOpen(true)}
                  >
                    <div className={styles.pagePreviewThumb}>
                      <Img
                        src={pageSrc}
                        alt={`Page ${maskId || page}`}
                        className={styles.pagePreviewImage}
                      />
                    </div>
                    <div className={styles.pagePreviewMeta}>
                      <span className={styles.pagePreviewMask}>Page {pageTitle ? pageTitle : maskId || page}</span>
                    </div>
                  </button>
                ) : (
                  <div className={styles.pagePreviewMaskOnly}>
                    <span className={styles.pagePreviewMask}>Page {maskId || page}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.headerToolbar}>
              <AppTextField
                label="Title"
                value={assignmentTitle}
                onChange={(e) => setAssignmentTitle(e.target.value)}
                InputLabelProps={{ shrink: true }}
                className={styles.headerFieldTitle}
              />
              <FormControl size="small" className={styles.headerFieldLang} fullWidth sx={{ maxWidth: { xs: '100%', sm: 220 } }}>
                <InputLabel id="builder-lang-feedback">AI feedback language</InputLabel>
                <Select
                  labelId="builder-lang-feedback"
                  label="AI feedback language"
                  value={languageFeedback}
                  onChange={(e) => setLanguageFeedback(String(e.target.value))}
                >
                  <MenuItem value="ENGLISH">English</MenuItem>
                  <MenuItem value="SPANISH">Spanish</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="success"
                className={styles.headerCreateBtn}
                onClick={handleCreateAssignment}
                disabled={
                  questions.length === 0 ||
                  disableSubmit ||
                  submitLoading ||
                  !currentUser?.id
                }
                sx={{ minHeight: { xs: 48, sm: 44 } }}
              >
                {submitLoading ? <CircularProgress size={22} color="inherit" /> : has(assignmentRecord, 'id') ? 'Update assignment' : 'Create assignment'}
              </Button>
            </div>
          </header>

          <div className={styles.scrollBody}>
            <div className={styles.grid}>
              {!compactBuilder ? <aside className={styles.sidebar}>{sidebarToolsNode}</aside> : null}

              <main className={styles.center}>
                <div ref={editSectionRef} className={styles.centerCard}>
                  {isSuperAdmin ? (
                    <section className={styles.builderConfigurationRegion} aria-label="Assignment configuration">
                      <AssignmentBuilderSuperAdminPanel
                        bookId={chapter}
                        pageId={page}
                        value={superAdminActivity}
                        onChange={patchSuperAdminActivity}
                      />
                    </section>
                  ) : null}
                  <section className={styles.builderQuestionRegion} aria-label="Questions">
                    <div className={styles.editQuestionHeader}>
                      <h2>Questions</h2>
                      {selectedQuestionOrdinal != null ? (
                        <span className={styles.editQuestionIndex}>Q{selectedQuestionOrdinal}</span>
                      ) : null}
                    </div>
                    {draft ? (
                      <>
                        <QuestionBuilderRenderer
                          key={`question-builder-${draft._uid}-${draft.type}`}
                          draft={draft}
                          setDraft={setDraftFromBuilder}
                          onPatch={handleUpdateDraft}
                          onTypeChange={(type) => {
                            setDraft((d) => {
                              if (!d) return null;
                              const next = applyTypeChange(d, type);
                              setQuestions((prev) => applyDraftToQuestions(prev, next));
                              return next;
                            });
                          }}
                          superAdminMode={isSuperAdmin}
                          bookPages={bookPagesForBuilder}
                          isStandardExamBook={isStandardExamBook}
                          standards={standards}
                          ebookGradeLevels={ebookGradeLevels}
                          enabledStateCodes={enabledStateCodes}
                        />
                      <div className={styles.actions}>
                        <button type="button" className={styles.btnUpdate} onClick={handleUpdate}>
                          + Update
                        </button>
                        <button
                          type="button"
                          className={styles.btnPreview}
                          onClick={() => setPreviewHtml((v) => !v)}
                        >
                          {previewHtml ? 'Hide preview' : 'Preview'}
                        </button>
                      </div>
                      {previewHtml && draftPreviewData && (
                        <div className={styles.previewEmbed}>
                          <AssignmentPreview
                            data={draftPreviewData}
                            onClose={() => setPreviewHtml(false)}
                            contenerHeight={draftPreviewHeight}
                            innerScrollStyle={{ paddingRight: 8 }}
                            showBottomBorder={false}
                            from={'assignmentBuilderToolPreview'}
                          />
                        </div>
                      )}
                      </>
                    ) : (
                      <div className={styles.emptyState}>Select or add a question.</div>
                    )}
                  </section>
                </div>
              </main>

              <AssignmentBuilderQuestionPreview
                layout="multi"
                questions={questions}
                selectedUid={selectedUid}
                onSelect={selectQuestion}
                onDelete={handleDelete}
                onDragEnd={handleDragEnd}
              />
            </div>
          </div>
          {compactBuilder ? (
            <Drawer
              anchor="left"
              open={toolsDrawerOpen}
              onClose={closeToolsDrawer}
              disablePortal
              ModalProps={{
                keepMounted: false,
                disableEnforceFocus: true,
                disableScrollLock: true,
              }}
              sx={{
                zIndex: 10,
                '& .MuiBackdrop-root': { zIndex: 9 },
                '& .MuiDrawer-paper': { zIndex: 10 },
              }}
              PaperProps={{
                sx: {
                  width: { xs: 'min(300px, 88vw)', sm: 300 },
                  pt: 2,
                  px: 2,
                  pb: 2,
                  boxSizing: 'border-box',
                },
              }}
            >
              <div className={styles.drawerToolsHeader}>Assignment tools</div>
              <div className={styles.drawerToolsBody}>{sidebarToolsNode}</div>
            </Drawer>
          ) : null}
        </div>
      </Modal>
      {open && assignmentAiModal && assignmentAiSource ? (
        <AssignmentAiSection
          data={assignmentAiSource.data}
          assignmentAiModal={assignmentAiModal}
          setassignmentAiModal={(next) => {
            setAssignmentAiModal(next);
            if (!next) setAssignmentAiSource(null);
          }}
          page={assignmentAiSource.page}
          chapter={assignmentAiSource.chapter}
          pageSrc={assignmentAiSource.pageSrc}
          content={assignmentAiSource.data?.content}
          fromResource={assignmentAiSource.fromResource}
          from={assignmentAiSource.from}
          generateVocabActivity={false}
          uniqueKeyAssignmentAi="assignmentBuilderToolAi"
          BALCON_PAGES={assignmentAiSource.balconPages}
          pageRenage={assignmentAiSource.pageRenage || {}}
          onAddQuestionsToAssignment={handleAiAdd as any}
          modalZIndex={Z_BUILDER_NESTED_MODAL}
        />
      ) : null}

      <AssignmentSourcePickerModal
        open={sourcePickerOpen}
        onClose={() => setSourcePickerOpen(false)}
        bookId={String(chapter)}
        pageId={String(page)}
        onSelectLocation={handleSourceLocationSelect}
        defaultResourceType="ebook"
        defaultSelectedItems={PICKER_EMPTY_ITEMS}
        defaultSelectedConfig={PICKER_EMPTY_CONFIG}
      />

      <AssignmentSourcePickerModal
        open={aiSourcePickerOpen}
        onClose={() => setAiSourcePickerOpen(false)}
        bookId={String(chapter)}
        pageId={String(page)}
        onSelectLocation={handleAiResourceSelect}
        dialogTitle="Select source for AI questions"
        defaultResourceType={aiSourcePickerDefaults.defaultResourceType}
        defaultSelectedItems={aiSourcePickerDefaults.defaultSelectedItems}
        defaultSelectedConfig={aiSourcePickerDefaults.defaultSelectedConfig}
      />

      <Dialog
        open={activityPickerOpen}
        onClose={() => setActivityPickerOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={dialogFullScreen}
        sx={{ zIndex: Z_BUILDER_DIALOG_ABOVE_APP_NAV }}
        PaperProps={{
          sx: dialogFullScreen
            ? {
                m: 0,
                maxHeight: '100dvh',
                height: '100dvh',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
              }
            : {
                width: 'min(90vw, 100%)',
                maxWidth: '90vw',
                maxHeight: '100dvh',
                height: 'min(90vh, 100dvh)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
              },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0, px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 }, pb: 1 }}>
          Select Questions From Activity
        </DialogTitle>
        <ThemeProvider theme={builderDialogMenuTheme}>
          <DialogContent
            className={styles.selectionDialogContent}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 0%',
              minHeight: 0,
              maxHeight: '100%',
              overflow: 'hidden',
              px: 0,
              pt: 0,
              pb: 0,
            }}
          >
            <div className={styles.selectionScrollBody}>
              <div className={styles.selectionGrid}>
                <div className={`${styles.selectionPane} ${styles.selectionPaneLeft}`}>
                  <div className={styles.selectionHeading}>Activities / Quizzes</div>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1.5,
                      px: '12px',
                      py: '12px',
                      alignItems: { sm: 'stretch' },
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
                      <AppTextField
                        fullWidth
                        placeholder="Search by title"
                        value={sourceAssignmentSearch}
                        onChange={(e) => setSourceAssignmentSearch(e.target.value)}
                        sx={{
                          padding: 0,
                          '@media (max-width:1024px)': {
                            '& .MuiOutlinedInput-input': {
                              padding: '12px 7px !important',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: '1 1 50%',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}
                    >
                      <FormControl size="small" fullWidth>
                        <InputLabel id="source-activity-sort-label">Sort activities</InputLabel>
                        <Select
                          labelId="source-activity-sort-label"
                          label="Sort activities"
                          value={sourceActivityListSort}
                          onChange={(e) =>
                            setSourceActivityListSort(e.target.value as SourceActivityListSort)
                          }
                          MenuProps={{
                            disablePortal: false,
                            slotProps: {
                              paper: {
                                sx: { zIndex: Z_BUILDER_DIALOG_MENU_THEME_MODAL + 1 },
                              },
                            },
                          }}
                        >
                          {SOURCE_ACTIVITY_SORT_SELECT_OPTIONS.map((opt) => (
                            <MenuItem
                              key={opt.value}
                              value={opt.value}
                              title={SOURCE_ACTIVITY_SORT_TOOLTIPS[opt.value]}
                            >
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                  <div className={styles.generateActivityLeftPanelActivitiesQuizzes}>
                  {sourceAssignmentsLoading ? (
                    <div
                      className={styles.selectionEmpty}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 180,
                      }}
                    >
                      <CircularProgress size={28} />
                    </div>
                  ) : filteredSourceAssignments.length === 0 ? (
                    <div className={styles.selectionEmpty}>
                      No activities or quizzes found for the selected book and page.
                    </div>

                  ) : selectedSourceResourceType === 'ebook' ? (
                    <div className={styles.selectionListLeftPanel}>
                      {ebookSourceAssignmentGroups.length > 0
                        ? ebookSourceAssignmentGroups.map((group) => renderSourceAssignmentGroup(group))
                        : filteredSourceAssignments.map((assignment) => renderSourceAssignmentButton(assignment))}
                    </div>
                  ) : (
                    <div className={styles.selectionList}>
                      {filteredSourceAssignments.map((assignment) => renderSourceAssignmentButton(assignment))}
                    </div>
                  )}
                  </div>
                </div>

                <div className={`${styles.selectionPane} ${styles.selectionPaneRight}`}>
                  <div className={styles.selectionHeading}>
                    Questions ({selectedImportQuestionKeys.length} selected)
                  </div>
                  <div className={styles.generateActivityRightQuestionPanel}>
                  {sourceAssignmentsLoading ? (
                    <div className={styles.selectionList}>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div key={index} className={styles.questionPickRow}>
                          <Skeleton variant="rounded" width={24} height={24} />
                          <div className={styles.questionPickContent}>
                            <Skeleton variant="text" width="35%" height={24} />
                            <Skeleton variant="text" width="100%" height={22} />
                            <Skeleton variant="text" width="82%" height={22} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : !selectedSourceAssignment ? (
                    <div className={styles.selectionEmpty}>Choose an activity from the left side.</div>
                  ) : (
                    <div className={styles.selectionListRightPanel}>
                      {selectedSourceAssignment.questions.map((question, index) => {
                        const questionKey = `${selectedSourceAssignment.id}-${index}`;
                        const checked = selectedImportQuestionKeys.includes(questionKey);
                        const text =
                          htmlToPlainPreviewText(String(question?.question || '')) ||
                          htmlToPlainPreviewText(String(question?.instruction || ''));
                        const questionTypeLabel = typeLabel(String(question?.type || 'essay'));
                        const { hasAudio, hasVideo, hasImage } = getQuestionMediaFlags(question);
                        return (
                          <label key={questionKey} className={styles.questionPickRow}>
                            <Checkbox
                              checked={checked}
                              onChange={() => handleToggleImportQuestion(questionKey)}
                            />
                            <div className={styles.questionPickContent}>
                              <div className={styles.questionPickHead}>
                                <div className={styles.questionPickTitle}>
                                  <span>Question {index + 1}</span>
                                  {hasAudio || hasVideo || hasImage ? (
                                    <span className={styles.questionPickIcons}>
                                      {hasAudio ? <AudioFileOutlined fontSize="inherit" /> : null}
                                      {hasVideo ? <VideocamOutlined fontSize="inherit" /> : null}
                                      {hasImage ? <ImageOutlined fontSize="inherit" /> : null}
                                    </span>
                                  ) : null}
                                </div>
                                <span className={styles.questionTypeBadge}>{questionTypeLabel}</span>
                              </div>
                              <div className={styles.questionPickText}>
                                {text || 'Untitled question'}
                              </div>
                              <button
                                type="button"
                                className={styles.questionPreviewBtn}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleOpenQuestionPreview(question, index);
                                }}
                              >
                                Preview
                              </button>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions
            sx={(t) => ({
              flexShrink: 0,
              flexWrap: 'wrap',
              gap: 1,
              px: { xs: 2, sm: 3 },
              pt: 1.5,
              pb: `max(12px, calc(12px + env(safe-area-inset-bottom, 0px)))`,
              borderTop: `1px solid ${t.palette.divider}`,
              bgcolor: 'background.paper',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              '& .MuiButton-root': {
                minHeight: 44,
                width: { xs: '100%', sm: 'auto' },
                ml: { xs: '0 !important', sm: undefined },
              },
            })}
          >
            <Button onClick={() => setActivityPickerOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleImportSelectedQuestions}
              disabled={sourceAssignmentsLoading || selectedImportQuestionKeys.length === 0}
            >
              Add Selected Questions ({selectedImportQuestionKeys.length})
            </Button>
          </DialogActions>
        </ThemeProvider>
      </Dialog>

      <Dialog
        open={pagePreviewOpen}
        onClose={() => setPagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={dialogFullScreen}
        sx={{ zIndex: Z_BUILDER_DIALOG_ABOVE_APP_NAV }}
        PaperProps={{
          sx: dialogFullScreen
            ? { m: 0, maxHeight: '100dvh', borderRadius: 0 }
            : undefined,
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <span>Page {maskId || page}</span>
          <IconButton
            onClick={() => setPagePreviewOpen(false)}
            aria-label="close page preview"
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', background: '#0f172a' }}>
          {pageSrc ? (
            <Img
              src={pageSrc}
              alt={`Page ${maskId || page}`}
              style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={coverPreviewOpen}
        onClose={() => setCoverPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={dialogFullScreen}
        sx={{ zIndex: Z_BUILDER_DIALOG_ABOVE_APP_NAV }}
        PaperProps={{
          sx: dialogFullScreen
            ? { m: 0, maxHeight: '100dvh', borderRadius: 0 }
            : undefined,
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Cover Preview</span>
          <IconButton onClick={() => setCoverPreviewOpen(false)} aria-label="close cover preview" size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', background: '#0f172a' }}>
          {coverPreviewSrc ? (
            <Img src={coverPreviewSrc} alt="Cover" style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(questionPreviewData)}
        onClose={() => setQuestionPreviewData(null)}
        maxWidth="md"
        fullWidth
        fullScreen={dialogFullScreen}
        sx={{ zIndex: Z_BUILDER_DIALOG_ABOVE_APP_NAV + 10 }}
        PaperProps={{
          sx: dialogFullScreen
            ? {
                m: 0,
                maxHeight: '100dvh',
                height: '100%',
                borderRadius: 0,
                overflow: 'hidden',
              }
            : {
                width: 'min(960px, calc(100vw - 32px))',
                maxWidth: '960px',
                maxHeight: 'calc(100dvh - 32px)',
                borderRadius: '16px',
                overflow: 'hidden',
              },
        }}
      >
        <DialogContent
          sx={{
            p: 2,
            background: '#f8fafc',
            overflowY: 'auto',
            flex: dialogFullScreen ? 1 : undefined,
            minHeight: 0,
          }}
        >
          {questionPreviewData ? (
            <AssignmentPreview
              data={questionPreviewData}
              from={'assignmentBuilderToolPreview'}
              onClose={() => setQuestionPreviewData(null)}
              contenerHeight={
                dialogFullScreen
                  ? Math.max(
                      typeof window !== 'undefined' ? window.innerHeight - 120 : 360,
                      280,
                    )
                  : Math.max(
                      typeof window !== 'undefined' ? window.innerHeight - 140 : 420,
                      420,
                    )
              }
              showBottomBorder={false}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={discardDialogOpen}
        onClose={handleDiscardDialogCancel}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'min(400px, calc(100vw - 32px))',
            maxWidth: '100%',
            m: 2,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          You have unsaved changes. Close without saving?
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleDiscardDialogCancel} color="inherit" variant="outlined">
            Keep editing
          </Button>
          <Button onClick={handleDiscardDialogConfirm} color="error" variant="contained">
            Close without saving
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AssignmentBuilderTool;
